import {
  Company,
  Country,
  Division,
  ExternalCompany,
  Group,
  PBIReport,
  Region,
  Role,
  Site,
  Standard,
  Subdivision,
  User
} from '@esg/esg-global-types';
import {
  Box,
  Collapse,
  FormControl,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from '@mui/material';
import React from 'react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BlockIcon from '@mui/icons-material/Block';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { MetricExtended, getMetrics } from '../../../lib/metric_capture/metric';
import { getSitesJoinParents } from '../../../lib/metric_capture/site';
import { getExternalCompanies } from '../../../lib/app/external_company';
import AlertBanner from '../../shared/banner/AlertBanner';
import { MetadataError } from '@ep/error-handling';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { SiteExtended } from '../../../lib/metric_capture/site';
import { getStandards } from '../../../lib/app/standard';
import SiteSelect from '../../shared/input/select/SiteSelect';
import PrimaryDivider from '../../shared/decoration/PrimaryDivider';
import {
  Module,
  View,
  resolveViewName,
  CUSTOM_DASHBOARD_VIEW_ID,
  hasCustomDashboard
} from '../../../lib/app/navigation';
import { CaptionText, MinorHeading, Subheading } from '../../shared/typography/PrimaryTypography';
import { getPBIReports } from '../../../lib/app/powerbi/pbi_report';
import PrimarySkeleton from '../../shared/loading/PrimarySkeleton';
import MetricSelect from '../../shared/input/select/MetricSelect';
import ExternalCompanySelect from '../../shared/input/select/ExternalCompanySelect';
import CountrySelect from '../../shared/input/select/CountrySelect';
import { getCountries } from '../../../lib/metric_capture/country';
import { getRegions } from '../../../lib/metric_capture/region';
import { getDivisions } from '../../../lib/metric_capture/division';
import { getSubdivisions } from '../../../lib/metric_capture/subdivision';
import RegionSelect from '../../shared/input/select/RegionSelect';
import DivisionSelect from '../../shared/input/select/DivisionSelect';
import SubdivisionSelect from '../../shared/input/select/SubdivisionSelect';
import {
  countModulePermissions,
  findViewPermissionAccess,
  getPermissionEntities,
  withoutViewPermission
} from '../../../util/user_access';
import {
  COMPONENT_UNKNOWN_FAILURE,
  FEEDBACK_ACTION_FAILURE,
  FUNCTION_UNKNOWN_FAILURE
} from '../../../util/global_variables';

interface CompanyAccessConfigProps {
  user_row: User;
  chosen_group: Group;
  chosen_company: Company;
  roles: Array<Role>;
  modules: Array<Module>;
  handleUserChange: (user_data: User) => void;
}

interface StandardNameMap {
  [key: string]: string;
}

const SOURCE = 'CompanyAccessConfig.tsx';

/**
 * A panel to allow users to update the company level permissions for a user from the parent flow.
 * @param {User} user_row User from parent flow being updated.
 * @param {Group} chosen_group Group to have user permissions updated for.
 * @param {Company} chosen_company Company to have user permissions updated for.
 * @param {Array<Role>} roles List of company roles updated user can have.
 * @param {Array<Module>} modules List of portal modules updated user can have permission assigned to.
 * @param {(user_data: User): void} handleUserChange Handler function for updating user data in parent.
 * @returns {JSX.Element}
 */
const CompanyAccessConfig = ({
  user_row,
  chosen_group,
  chosen_company,
  roles,
  modules,
  handleUserChange
}: CompanyAccessConfigProps) => {
  const { setFeedbackData } = React.useContext(FeedbackSnackbarContext);
  const [metrics, setMetrics] = React.useState<Array<MetricExtended>>([]);
  const [countries, setCountries] = React.useState<Array<Country>>([]);
  const [regions, setRegions] = React.useState<Array<Region>>([]);
  const [divisions, setDivisions] = React.useState<Array<Division>>([]);
  const [subdivisions, setSubdivisions] = React.useState<Array<Subdivision>>([]);
  const [sites, setSites] = React.useState<Array<SiteExtended>>([]);
  const [externalCompanies, setExternalCompanies] = React.useState<Array<ExternalCompany>>([]);
  const [pbiReports, setPBIReports] = React.useState<Array<PBIReport>>([]);
  const [expandedModule, setExpandedModule] = React.useState<string>('');
  const [entitiesLoading, setEntitiesLoading] = React.useState<boolean>(false);

  const none_role: Role = {
    id: 'none',
    deleted: null,
    name: 'None'
  };

  // Only allow custom or none roles for external users.
  const allowed_roles: Array<Role> = [
    ...roles.filter((role: Role) => (user_row.type === 'external' ? role.id === 'custom' : true)),
    none_role
  ];

  const company_permissions: Array<string> =
    user_row.groups?.[chosen_group.id]?.companies?.[chosen_company.id]?.permissions ?? [];

  // Update the user_row's role attribute for the currently selected company.
  const handleChangeCompanyRole = (group_id: string, company_id: string, role: string): void => {
    if (role === 'none') {
      const new_user_data: User = { ...user_row };
      if (new_user_data.groups?.[group_id]?.companies?.[company_id]) {
        delete new_user_data.groups[group_id].companies?.[company_id];
        handleUserChange(new_user_data);
      }
    } else {
      handleUserChange({
        ...user_row,
        groups: {
          ...user_row.groups,
          [group_id]: {
            ...user_row.groups?.[group_id],
            companies: {
              ...user_row.groups?.[group_id]?.companies,
              [company_id]: {
                ...user_row.groups?.[group_id]?.companies?.[company_id],
                role: role
              }
            }
          }
        }
      });
    }
  };

  // Read through the same matcher `getViewAccess` uses. Previously this found by unanchored
  // prefix, so for a user granted only Refrigeration Plants (CaaS) the grid displayed Read
  // against Refrigeration Plants — a view they had never been given. The grid and enforcement
  // must agree, or the grid is actively misleading about what a user can see.
  const getModulePermission = (module: string, view: string): string | undefined =>
    findViewPermissionAccess(
      user_row.groups?.[chosen_group.id]?.companies?.[chosen_company.id]?.permissions ?? [],
      `${module}.${view}`
    );

  const handleChangeModulePermissions = (module: string, view: string, access: string): void => {
    // Removing by unanchored prefix also stripped the sibling view whose id extends this one,
    // so editing Refrigeration Plants silently revoked Refrigeration Plants (CaaS).
    const new_permissions: Array<string> = withoutViewPermission(
      company_permissions,
      `${module}.${view}`
    );
    if (access !== 'none') {
      new_permissions.push(`${module}.${view}.${access}`);
    }
    handleUserChange({
      ...user_row,
      groups: {
        ...user_row.groups,
        [chosen_group.id]: {
          ...user_row.groups[chosen_group.id],
          companies: {
            ...(user_row.groups?.[chosen_group.id]?.companies ?? {}),
            [chosen_company.id]:
              user_row.groups?.[chosen_group.id]?.companies?.[chosen_company.id] !== undefined
                ? {
                    ...user_row.groups[chosen_group.id].companies![chosen_company.id],
                    permissions: new_permissions
                  }
                : {
                    role: 'basic',
                    permissions: new_permissions
                  }
          }
        }
      }
    });
  };

  const handleChangeEntityPermissions = (
    permissions: Array<string>,
    permission_entity: string
  ): void => {
    const new_permissions: Array<string> = permissions.map((id) => `${permission_entity}.${id}`);
    const unrelated_permissions: Array<string> =
      company_permissions.filter((permission) => !permission.startsWith(`${permission_entity}.`)) ??
      [];
    handleUserChange({
      ...user_row,
      groups: {
        ...user_row.groups,
        [chosen_group.id]: {
          ...user_row.groups[chosen_group.id],
          companies: {
            ...(user_row.groups?.[chosen_group.id]?.companies ?? {}),
            [chosen_company.id]:
              user_row.groups?.[chosen_group.id]?.companies?.[chosen_company.id] !== undefined
                ? {
                    ...user_row.groups[chosen_group.id].companies![chosen_company.id],
                    permissions: [...unrelated_permissions, ...new_permissions]
                  }
                : {
                    role: 'custom',
                    permissions: [...unrelated_permissions, ...new_permissions]
                  }
          }
        }
      }
    });
  };

  const fetchEntities = async () => {
    setEntitiesLoading(true);
    try {
      const [
        metrics,
        countries,
        regions,
        divisions,
        subdivisions,
        sites,
        external_companies,
        standards,
        pbi_reports
      ]: [
        Array<MetricExtended & { standard_name?: string }>,
        Array<Country>,
        Array<Region>,
        Array<Division>,
        Array<Subdivision>,
        Array<SiteExtended>,
        Array<ExternalCompany>,
        Array<Standard>,
        Array<PBIReport>
      ] = await Promise.all([
        getMetrics(false, chosen_group.id, chosen_company.id),
        getCountries(false, chosen_group.id, chosen_company.id),
        getRegions(false, chosen_group.id, chosen_company.id),
        getDivisions(chosen_group.id, chosen_company.id),
        getSubdivisions(chosen_group.id, chosen_company.id),
        getSitesJoinParents(chosen_group.id, chosen_company.id),
        getExternalCompanies(chosen_group.id, chosen_company.id),
        getStandards(chosen_group.id, chosen_company.id),
        getPBIReports(chosen_group.id, chosen_company.id)
      ]);
      const ref_name_standard_map: StandardNameMap = {};
      for (const standard of standards) {
        ref_name_standard_map[standard.id] = standard.name;
      }
      metrics.map((metric, index) => {
        const standard_string: string =
          ref_name_standard_map[metric.standard ? metric.standard.id : ''];
        metrics[index] = {
          ...metrics[index],
          standard_name: standard_string
        };
      });
      setMetrics(metrics);
      setCountries(countries);
      setRegions(regions);
      setDivisions(divisions);
      setSubdivisions(subdivisions);
      setSites(sites);
      setExternalCompanies(external_companies);
      setPBIReports(pbi_reports);
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'fetchEntities'),
          {
            chosen_group: chosen_group,
            chosen_company: chosen_company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE(
          `Unable to fetch metrics and sites for ${chosen_company.name}`,
          tracking_id
        ),
        state: true,
        type: 'error'
      });
    } finally {
      setEntitiesLoading(false);
    }
  };

  React.useEffect(() => {
    try {
      (async () => {
        await fetchEntities().catch((error) => {
          throw new Error(error);
        });
      })().catch((error) => {
        throw new Error(error);
      });
    } catch (err: unknown) {
      const tracking_id: string = generateUuidV4();
      log(
        'error',
        new MetadataError(
          err instanceof Error ? err.message : COMPONENT_UNKNOWN_FAILURE(SOURCE),
          {
            chosen_group: chosen_group,
            chosen_company: chosen_company
          },
          tracking_id
        )
      );
      setFeedbackData({
        message: FEEDBACK_ACTION_FAILURE(
          `Unable to fetch metrics and sites for ${chosen_company.name}`,
          tracking_id
        ),
        state: true,
        type: 'error'
      });
    }
  }, []);

  return (
    <Box>
      <Subheading weight="Bold" sx={{ mb: 2 }}>
        Configure {chosen_company ? chosen_company.name : 'Company'} Access for{' '}
        {user_row.first_name ? user_row.first_name : 'User'}
      </Subheading>
      <Box sx={{ mt: 1 }}>
        <Grid container spacing={2}>
          <Grid size={10}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Typography sx={{ minWidth: 80 }}>Access</Typography>
              <FormControl>
                <Select
                  value={
                    user_row.groups?.[chosen_group.id]?.companies?.[chosen_company.id]?.role ??
                    'none'
                  }
                  displayEmpty
                  inputProps={{ 'aria-label': 'Without label' }}
                  onChange={(event) =>
                    handleChangeCompanyRole(chosen_group.id, chosen_company.id, event.target.value)
                  }
                  sx={{ minWidth: 200 }}
                >
                  {allowed_roles.map((role: Role) => {
                    return (
                      <MenuItem key={role.id} value={role.id}>
                        {role.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          {user_row.groups[chosen_group.id]?.companies?.[chosen_company.id]?.role === 'viewer' && (
            <Box sx={{ my: 4 }}>
              <AlertBanner
                severity="info"
                open
                message={`${user_row.email} has Viewer access for ${chosen_company.name} and can view all dashboard, metric, site and external company data.`}
              />
            </Box>
          )}
          {user_row.groups[chosen_group.id]?.companies?.[chosen_company.id]?.role === 'editor' && (
            <Box sx={{ my: 4 }}>
              <AlertBanner
                severity="info"
                open
                message={`${user_row.email} has Editor access for ${chosen_company.name} and can view all dashboard, metric, site and external company data
                 as well as create, update and delete relevant module data.`}
              />
            </Box>
          )}
          {user_row.groups[chosen_group.id]?.companies?.[chosen_company.id] === undefined && (
            <Box sx={{ my: 4 }}>
              <AlertBanner
                severity="info"
                open
                message={`${user_row.email} has No Access for ${chosen_company.name}, set access level above to configure`}
              />
            </Box>
          )}
          {user_row.groups[chosen_group.id]?.companies?.[chosen_company.id]?.role === 'custom' && (
            <>
              <Grid size={6}>
                <MinorHeading sx={{ my: 2 }}>Module Permissions</MinorHeading>
                <PrimaryDivider opacity={0.7} sx={{ mb: 1 }} />
                {entitiesLoading ? (
                  <PrimarySkeleton variant="rounded" width={'100%'} sx={{ my: 2, height: 60 }} />
                ) : (
                  // Module List
                  <List sx={{ width: '100%' }} component="nav">
                    {modules.map((module, index) => {
                      const valid_views: Array<View> = module.views.filter((view: View) => {
                        const pbiValid = Boolean(
                          !view.require_pbi_report ||
                          !!pbiReports.find((pbi_report: PBIReport) => pbi_report.view === view.id)
                        );
                        const externalUserValid = Boolean(
                          user_row.type !== 'external' || view.allow_external_users
                        );
                        const isAvailable =
                          view.id !== CUSTOM_DASHBOARD_VIEW_ID ||
                          hasCustomDashboard(chosen_company.id);
                        return pbiValid && externalUserValid && isAvailable;
                      });
                      return (
                        <React.Fragment key={`module_${index}`}>
                          {/* Module Item */}
                          <ListItemButton
                            onClick={() =>
                              setExpandedModule(expandedModule === module.id ? '' : module.id)
                            }
                          >
                            <ListItemText primary={module.name} />
                            {/* Module Views Count Badge */}
                            <Typography
                              sx={{
                                backgroundColor: '#E7E8E9',
                                color: '#454545',
                                borderRadius: 3,
                                px: 2,
                                mr: 4,
                                whiteSpace: 'nowrap',
                                textAlign: 'center',
                                width: '5%'
                              }}
                            >
                              {countModulePermissions(company_permissions, module.id)}
                            </Typography>
                            {/* Module List Item Expand / Collapse Button */}
                            {expandedModule === module.id ? <ExpandLess /> : <ExpandMore />}
                          </ListItemButton>
                          <Collapse in={expandedModule === module.id} timeout="auto" unmountOnExit>
                            {/* View List */}
                            <List component="div" disablePadding>
                              {valid_views.length > 0 ? (
                                valid_views.map((view: View) => {
                                  return (
                                    // View Item
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        mb: 1,
                                        alignItems: 'center',
                                        pl: 4,
                                        width: '80%'
                                      }}
                                      key={view.id}
                                    >
                                      <Typography fontSize={'0.9rem'}>
                                        {resolveViewName(view, chosen_company.id)}
                                      </Typography>
                                      {/* View Access Select */}
                                      <FormControl sx={{ pl: 2, ml: 'auto' }}>
                                        <Select
                                          value={getModulePermission(module.id, view.id) ?? 'none'}
                                          displayEmpty
                                          inputProps={{ 'aria-label': 'Without label' }}
                                          renderValue={(value) => {
                                            const display_value = (
                                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                {value === 'write' && <EditIcon />}
                                                {value === 'read' && <VisibilityIcon />}
                                                {value === 'none' && <BlockIcon />}
                                              </Box>
                                            );
                                            return display_value;
                                          }}
                                          onChange={(event) =>
                                            handleChangeModulePermissions(
                                              module.id,
                                              view.id,
                                              event.target.value
                                            )
                                          }
                                          disableUnderline
                                          variant="standard"
                                        >
                                          <MenuItem value={'read'}>Read</MenuItem>
                                          {view.allow_write_access && (
                                            <MenuItem value={'write'}>Write</MenuItem>
                                          )}
                                          <MenuItem value={'none'}>None</MenuItem>
                                        </Select>
                                      </FormControl>
                                    </Box>
                                  );
                                })
                              ) : (
                                <CaptionText
                                  sx={{ my: 2, pl: 4 }}
                                >{`No configured views for ${chosen_company.name} - ${module.name}`}</CaptionText>
                              )}
                            </List>
                          </Collapse>
                          <PrimaryDivider opacity={0.2} sx={{ my: 1 }} />
                        </React.Fragment>
                      );
                    })}
                  </List>
                )}
              </Grid>
              <Grid size={6}>
                <MinorHeading sx={{ my: 2 }}>Entity Access</MinorHeading>
                <PrimaryDivider opacity={0.7} sx={{ mb: 1 }} />
                <Box sx={{ pl: 2 }}>
                  {entitiesLoading ? (
                    <PrimarySkeleton variant="rounded" width={'100%'} sx={{ my: 2, height: 60 }} />
                  ) : (
                    <>
                      <Box sx={{ mb: 4 }}>
                        <MetricSelect
                          options={metrics}
                          multi_select
                          disable_selected_tags
                          group_by="metric_group.name"
                          input_label="Metrics"
                          select_all
                          clearable={false}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            metrics,
                            'metric'
                          )}
                          handleChangeMetrics={(
                            metrics: MetricExtended | Array<MetricExtended> | null
                          ) => {
                            if (Array.isArray(metrics)) {
                              handleChangeEntityPermissions(
                                metrics.map((metric: MetricExtended) => metric.id),
                                'metric'
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 4 }}>
                        <CountrySelect
                          multi_select
                          select_all
                          clearable={false}
                          disable_selected_tags
                          tag_limit={3}
                          options={countries}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            countries,
                            'country'
                          )}
                          variant="standard"
                          handleChangeCountries={(countries: Country | Array<Country> | null) => {
                            if (Array.isArray(countries)) {
                              handleChangeEntityPermissions(
                                countries.map((country: Country) => country.id),
                                'country'
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 4 }}>
                        <RegionSelect
                          multi_select
                          select_all
                          clearable={false}
                          disable_selected_tags
                          tag_limit={3}
                          group_by="country.name"
                          options={regions}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            regions,
                            'region'
                          )}
                          handleChangeRegions={(regions: Region | Array<Region> | null) => {
                            if (Array.isArray(regions)) {
                              handleChangeEntityPermissions(
                                regions.map((region: Region) => region.id),
                                'region'
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 4 }}>
                        <DivisionSelect
                          multi_select
                          select_all
                          clearable={false}
                          disable_selected_tags
                          tag_limit={3}
                          options={divisions}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            divisions,
                            'division'
                          )}
                          handleChangeDivisions={(divisions: Division | Array<Division> | null) => {
                            if (Array.isArray(divisions)) {
                              handleChangeEntityPermissions(
                                divisions.map((division: Division) => division.id),
                                'division'
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 4 }}>
                        <SubdivisionSelect
                          multi_select
                          select_all
                          clearable={false}
                          disable_selected_tags
                          tag_limit={3}
                          group_by="division.name"
                          options={subdivisions}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            subdivisions,
                            'subdivision'
                          )}
                          handleChangeSubdivisions={(
                            subdivisions: Subdivision | Array<Subdivision> | null
                          ) => {
                            if (Array.isArray(subdivisions)) {
                              handleChangeEntityPermissions(
                                subdivisions.map((subdivision: Subdivision) => subdivision.id),
                                'subdivision'
                              );
                            }
                          }}
                        />
                      </Box>
                      <Box sx={{ mb: 4 }}>
                        <SiteSelect
                          multi_select
                          select_all
                          clearable={false}
                          disable_selected_tags
                          tag_limit={3}
                          group_by="subdivision.name"
                          options={sites}
                          selected_options={getPermissionEntities(
                            company_permissions,
                            sites,
                            'site'
                          )}
                          handleChangeSites={(
                            sites: SiteExtended | Site | Array<SiteExtended | Site> | null
                          ) => {
                            if (Array.isArray(sites)) {
                              handleChangeEntityPermissions(
                                sites.map((site: SiteExtended | Site) => site.id),
                                'site'
                              );
                            }
                          }}
                        />
                      </Box>
                      {(user_row.type === 'any' || user_row.type === 'external') && (
                        <Box sx={{ mb: 4 }}>
                          <ExternalCompanySelect
                            multi_select
                            select_all
                            clearable={false}
                            disable_selected_tags
                            tag_limit={3}
                            options={externalCompanies}
                            selected_options={getPermissionEntities(
                              company_permissions,
                              externalCompanies,
                              'external_company'
                            )}
                            handleChangeExternalCompanies={(
                              external_companies: ExternalCompany | Array<ExternalCompany> | null
                            ) => {
                              if (Array.isArray(external_companies)) {
                                handleChangeEntityPermissions(
                                  external_companies.map(
                                    (external_company: ExternalCompany) => external_company.id
                                  ),
                                  'external_company'
                                );
                              }
                            }}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default CompanyAccessConfig;
